import { useEffect, type RefObject } from "react";

// DocuSeal scrolls inside its own shadow-DOM panes, which trackpad scroll does
// not reach reliably when embedded. Defeat its internal height caps so the
// component grows to its content height and a plain overflow-auto parent
// becomes the single, reliable scroller.
const SHADOW_EXPAND_CSS = `
  :host { height: auto !important; max-height: none !important; min-height: 0 !important; }
  .main-container { max-height: none !important; height: auto !important; }
  [class*="overflow-y-auto"], [class*="overflow-y-hidden"] {
    overflow-y: visible !important;
    max-height: none !important;
    height: auto !important;
  }
`;

/**
 * Embed a DocuSeal web component (`<docuseal-builder>` / `<docuseal-form>`):
 * inject its script once (guarded by URL) and expand its shadow-DOM height
 * caps. Shared by the builder and signing-form embeds.
 */
export function useDocusealEmbed({
  wrapperRef,
  elementTag,
  scriptUrl,
  resetKey,
}: {
  wrapperRef: RefObject<HTMLElement | null>;
  elementTag: "docuseal-builder" | "docuseal-form";
  scriptUrl: string;
  /** Re-run shadow expansion when the embedded document changes (token/src). */
  resetKey: string;
}) {
  // Inject the script once; guard against double-injection (keyed by URL).
  useEffect(() => {
    const attr = "data-docuseal-src";
    if (!document.querySelector(`script[${attr}="${scriptUrl}"]`)) {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.setAttribute(attr, scriptUrl);
      script.async = true;
      document.head.appendChild(script);
    }
  }, [scriptUrl]);

  // Poll for the web component's shadowRoot, then inject the expansion style.
  useEffect(() => {
    let cancelled = false;
    function inject() {
      if (cancelled) {
        return;
      }
      const host = wrapperRef.current?.querySelector(elementTag);
      const sr = host?.shadowRoot;
      if (!sr) {
        setTimeout(inject, 100);
        return;
      }
      if (sr.querySelector("style[data-saile-expand]")) {
        return;
      }
      const style = document.createElement("style");
      style.setAttribute("data-saile-expand", "");
      style.textContent = SHADOW_EXPAND_CSS;
      sr.appendChild(style);
    }
    inject();
    return () => {
      cancelled = true;
    };
  }, [wrapperRef, elementTag, resetKey]);
}
